import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Upload, ChevronDown, ChevronRight, Edit2, Check, X, GripVertical } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ✅ 正确的 dnd-kit 导入
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

// 拖拽章节组件
const SortableSection = ({ section, sIdx, expandedSections, setExpandedSections, onDelete, onAddLesson, addingLessonToSection, setAddingLessonToSection, newLesson, setNewLesson, selectedFile, setSelectedFile, onUploadLesson, uploading, uploadProgress, onDeleteLesson }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg overflow-hidden">
      {/* 章节头 */}
      <div className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer"
        onClick={() => setExpandedSections(prev => ({ ...prev, [section._id]: !prev[section._id] }))}>
        <div className="flex items-center space-x-3 flex-1">
          {/* 拖拽手柄 */}
          <div {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600">
            <GripVertical className="h-5 w-5" />
          </div>
          {expandedSections[section._id]
            ? <ChevronDown className="h-5 w-5 text-gray-400" />
            : <ChevronRight className="h-5 w-5 text-gray-400" />}
          <span className="font-medium">
            第{sIdx + 1}章：{section.title}
          </span>
          <span className="text-xs text-gray-400">({(section.lessons || []).length} 课时)</span>
        </div>
        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onAddLesson(addingLessonToSection === section._id ? null : section._id)}
            className="text-xs text-primary-600 hover:text-primary-800 flex items-center"
          >
            <Plus className="h-3 w-3 mr-1" />添加课时
          </button>
          <button
            onClick={() => onDelete(section._id)}
            className="text-xs text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* 添加课时表单 */}
      {addingLessonToSection === section._id && (
        <div className="p-4 bg-yellow-50 border-t">
          <h4 className="text-sm font-medium mb-3">为「{section.title}」添加课时</h4>
          <form onSubmit={(e) => onUploadLesson(e, section._id)} className="space-y-3">
            <input
              type="text"
              value={newLesson.title}
              onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
              placeholder="课时标题"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            <textarea
              value={newLesson.description}
              onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
              placeholder="课时描述（可选）"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
            />
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`isFree-${section._id}`}
                checked={newLesson.isFree}
                onChange={(e) => setNewLesson({ ...newLesson, isFree: e.target.checked })}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <label htmlFor={`isFree-${section._id}`} className="ml-2 text-sm text-gray-700">
                设为免费试看
              </label>
            </div>
            <div>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
              <p className="text-xs text-gray-400 mt-1">支持 MP4, WebM, MOV，最大 2GB</p>
            </div>
            {uploading && (
              <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>上传中...</span><span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
              </div>
            )}
            <div className="flex gap-2">
              <button type="submit" disabled={uploading} className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 flex items-center">
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? `上传中 ${uploadProgress}%` : '上传并添加'}
              </button>
              <button type="button" onClick={() => { setAddingLessonToSection(null); setNewLesson({ title: '', description: '', isFree: false }); setSelectedFile(null); }} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                取消
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 课时列表 */}
      {expandedSections[section._id] && (
        <div className="divide-y">
          {(section.lessons || []).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">暂无课时</p>
          ) : (
            (section.lessons || []).map((lesson, lIdx) => (
              <div key={lesson._id} className="flex items-center justify-between p-3 px-6 hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-400 w-6">{lIdx + 1}</span>
                  <div>
                    <p className="text-sm font-medium">{lesson.title}</p>
                    {lesson.isFree && (
                      <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded">免费</span>
                    )}
                  </div>
                </div>
                <button onClick={() => onDeleteLesson(lesson._id)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CourseManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});

  // 新增章节
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);

  // 新增课时（每个章节独立表单）
  const [addingLessonToSection, setAddingLessonToSection] = useState(null);
  const [newLesson, setNewLesson] = useState({ title: '', description: '', isFree: false });
  const [selectedFile, setSelectedFile] = useState(null);

  // 拖拽传感器
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const data = await api.get(`/courses/${id}`);
      if (data.success) {
        setCourse(data.data);
        // 默认展开所有章节
        const expanded = {};
        (data.data.sections || []).forEach((s) => { expanded[s._id] = true; });
        setExpandedSections(expanded);
      }
    } catch (error) {
      toast.error('获取课程失败');
    } finally {
      setLoading(false);
    }
  };

  // 添加章节
  const handleAddSection = async () => {
    if (!newSectionTitle.trim()) { toast.error('请输入章节标题'); return; }
    try {
      const data = await api.post(`/courses/${id}/sections`, { title: newSectionTitle.trim(), description: '' });
      if (data.success) {
        toast.success('章节添加成功');
        setNewSectionTitle('');
        setShowAddSection(false);
        fetchCourse();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || '添加失败');
    }
  };

  // 删除章节
  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('确认删除该章节及其所有课时？')) return;
    try {
      const data = await api.delete(`/courses/${id}/sections/${sectionId}`);
      if (data.success) { toast.success('章节已删除'); fetchCourse(); }
      else { toast.error(data.message || '删除失败'); }
    } catch (error) {
      toast.error((error && error.message) || (error && error.response && error.response.data && error.response.data.message) || '删除失败');
    }
  };

  // 添加课时（带视频上传）
  const handleAddLesson = async (e, sectionId) => {
    e.preventDefault();
    if (!selectedFile) { toast.error('请选择视频文件'); return; }
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('video', selectedFile);
    formData.append('title', newLesson.title);
    formData.append('description', newLesson.description);
    formData.append('isFree', newLesson.isFree);
    formData.append('duration', '0');
    if (sectionId) {
      formData.append('sectionId', sectionId);
    }
    try {
      const data = await api.post(`/courses/${id}/lessons`, formData, {
        timeout: 600000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      if (data.success) {
        toast.success('课时添加成功');
        setNewLesson({ title: '', description: '', isFree: false });
        setSelectedFile(null);
        setAddingLessonToSection(null);
        fetchCourse();
      } else {
        toast.error(data.message || '添加失败');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  // 删除课时
  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('确认删除该课时？')) return;
    try {
      const data = await api.delete(`/courses/${id}/lessons/${lessonId}`);
      if (data.success) {
        toast.success('课时已删除');
        fetchCourse();
      } else {
        toast.error(data.message || '删除失败');
      }
    } catch (error) {
      toast.error((error && error.message) || (error && error.response && error.response.data && error.response.data.message) || '删除失败');
    }
  };

  // 拖拽结束
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sections = [...(course.sections || [])];
    const oldIndex = sections.findIndex((s) => s._id === active.id);
    const newIndex = sections.findIndex((s) => s._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // 交换位置
    const [removed] = sections.splice(oldIndex, 1);
    sections.splice(newIndex, 0, removed);

    // 更新 order 字段
    const updates = sections.map((s, idx) => ({
      id: s._id,
      order: idx,
    }));

    // 乐观更新 UI
    setCourse({ ...course, sections });

    try {
      const data = await api.put(`/courses/${id}/sections/reorder`, { sections: updates });
      if (data.success) {
        toast.success('章节顺序已更新');
      } else {
        toast.error(data.message || '更新顺序失败');
        fetchCourse(); // 回滚
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || '更新顺序失败');
      fetchCourse(); // 回滚
    }
  };

  // 统计总课时数（兼容sections和老lessons字段）
  const getTotalLessons = () => {
    if (!course) return 0;
    const fromSections = (course.sections || []).reduce((acc, s) => acc + (s.lessons || []).length, 0);
    const fromLessons = fromSections === 0 ? (course.lessons || []).length : 0;
    return fromSections + fromLessons;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  if (!course) {
    return <div className="text-center py-12 text-gray-500">课程不存在</div>;
  }

  const sections = course.sections || [];

  return (
    <div className="space-y-6">
      {/* 顶部标题 & 操作 */}
      <div className="flex justify-between items-start">
        <div>
          <button onClick={() => navigate('/instructor')} className="text-gray-500 hover:text-gray-700 text-sm mb-2 flex items-center">
            ← 返回课程列表
          </button>
          <h1 className="text-2xl font-bold">{course.title}</h1>
        </div>
      </div>

      {/* 课程概况 */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-500">章节数</p>
            <p className="text-2xl font-bold">{sections.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">总课时</p>
            <p className="text-2xl font-bold">{getTotalLessons()}</p>
          </div>
        </div>
      </div>

      {/* 章节列表 */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">章节管理（支持拖拽排序）</h2>
          <button
            onClick={() => setShowAddSection(!showAddSection)}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center text-sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            添加章节
          </button>
        </div>

        {/* 添加章节表单 */}
        {showAddSection && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg flex gap-2">
            <input
              type="text"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              placeholder="章节标题，如：第一章 账号注册"
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
            />
            <button onClick={handleAddSection} className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center">
              <Check className="h-4 w-4 mr-1" />确认
            </button>
            <button onClick={() => { setShowAddSection(false); setNewSectionTitle(''); }} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {sections.length === 0 ? (
          <p className="text-gray-500 text-center py-8">暂无章节，请先添加章节</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((s) => s._id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {sections.map((section, sIdx) => (
                  <SortableSection
                    key={section._id}
                    section={section}
                    sIdx={sIdx}
                    expandedSections={expandedSections}
                    setExpandedSections={setExpandedSections}
                    onDelete={handleDeleteSection}
                    onAddLesson={setAddingLessonToSection}
                    addingLessonToSection={addingLessonToSection}
                    setAddingLessonToSection={setAddingLessonToSection}
                    newLesson={newLesson}
                    setNewLesson={setNewLesson}
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile}
                    onUploadLesson={handleAddLesson}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    onDeleteLesson={handleDeleteLesson}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* 兼容旧格式：如果有 lessons 但没有 sections，显示旧列表 */}
      {sections.length === 0 && (course.lessons || []).length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-bold mb-4">课时列表（旧格式）</h2>
          <p className="text-sm text-orange-600 mb-4">⚠️ 该课程使用旧结构，建议添加章节后重新整理</p>
          <div className="space-y-2">
            {course.lessons.map((lesson, index) => (
              <div key={lesson._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">{index + 1}</span>
                  <span className="text-sm font-medium">{lesson.title}</span>
                  {lesson.isFree && <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded">免费</span>}
                </div>
                <button onClick={() => handleDeleteLesson(lesson._id)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManager;
